import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { DocumentTextIcon } from '@heroicons/react/outline';

import PrivateRoute from 'components/Navigation/ProtectedRoute';
import BaseInput from 'components/Inputs/BaseInput';
import { showErrorToast, showSuccessToast } from 'components/Toast';
import SolidButton from 'components/SolidButton';

import { validateFile } from 'utils/fileUtils';
import { REQUIRED_RULE } from 'utils/constants';
import { BUTTON_COLORS } from 'utils/enums';
import GeneralApi from 'utils/generalApi';
import { useAuth } from 'contexts/Auth';

export default function AdminLandingPage() {
  const {
    register,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm();
  const history = useNavigate();
  const auth = useAuth();

  const generalApi = useMemo(() => new GeneralApi(auth, history, 'user'), [auth, history]);
  const adminGeneralApi = useMemo(() => new GeneralApi(auth, history), [auth, history]);

  const [loading, setLoading] = useState(false);

  const [documentPnUrl, setDocumentPnUrl] = useState('');
  const [documentPjUrl, setDocumentPjUrl] = useState('');
  const [documentPrivacyUrl, setDocumentPrivacyUrl] = useState('');

  const [documentPn, setDocumentPn] = useState<File>();
  const [documentPj, setDocumentPj] = useState<File>();
  const [documentPrivacy, setDocumentPrivacy] = useState<File>();

  const saveFileInfo = async (field: string) => {
    const file =
      field === 'tyc_pn' ? documentPn : field === 'tyc_pj' ? documentPj : documentPrivacy;
    if (!file) return;
    const finalData = new FormData();
    finalData.append('field', field);
    finalData.append('tyc', file);
    setLoading(true);
    const result = await adminGeneralApi.put('/edit_tyc', finalData);
    if (!result.success) {
      setLoading(false);
      showErrorToast(result.message);
      return;
    }
    setLoading(false);
    showSuccessToast('Contenido actualizado');
  };

  const saveInfo = async (field: string) => {
    setLoading(true);
    const result = await adminGeneralApi.put('/webinfo', { field, content: getValues(field) });
    if (!result.success) {
      setLoading(false);
      showErrorToast(result.message);
      return;
    }
    setLoading(false);
    showSuccessToast('Contenido actualizado');
  };

  const checkFile = async (event: React.FormEvent<HTMLInputElement>, field: string) => {
    const target = event.target as HTMLInputElement;
    let isValid = validateFile(target, 'pdf');
    if (!isValid) {
      setDocumentPj(undefined);
      showErrorToast('Formato de documento no válido, debe ser un archivo .pdf');
      return;
    }
    if (field === 'documentPn') {
      setDocumentPn(target?.files?.[0]);
    } else if (field === 'documentPj') {
      setDocumentPj(target?.files?.[0]);
    } else if (field === 'documentPrivacy') {
      setDocumentPrivacy(target?.files?.[0]);
    }
  };

  const buildBaseInput = (
    label: string,
    type: React.HTMLInputTypeAttribute,
    name: string,
    rules: any,
    props: any
  ) => {
    return (
      <BaseInput
        label={label}
        type={type}
        name={name}
        register={register}
        watch={watch}
        errors={errors}
        rules={rules}
        {...props}
      />
    );
  };

  useEffect(() => {
    const getWebInfo = async () => {
      const result = await generalApi.get('/webinfos');
      if (!result.success) {
        showErrorToast(result.message);
        return;
      }
      for (let i = 0; i < result.data.length; i++) {
        const element = result.data[i];
        if (['about', 'fl1', 'fl2', 'banner'].includes(element.field)) {
          setValue(element.field, element.info);
        }
        if (element.field === 'tyc_pn') {
          setDocumentPnUrl(element.info);
        } else if (element.field === 'tyc_pj') {
          setDocumentPjUrl(element.info);
        } else if (element.field === 'pp') {
          setDocumentPrivacyUrl(element.info);
        }
      }
    };
    getWebInfo();
  }, [generalApi, setValue]);

  return (
    <PrivateRoute>
      <div className="text-[#1B205A]">
        <div className="pb-8 2xl:px-24">
          <div className="mt-6 justify-between space-y-4 px-8 pb-4 sm:flex sm:space-y-0">
            <p className="text-2xl font-bold">Landing Manager</p>
          </div>
          <div className="gap-x-12 gap-y-8 sm:grid sm:grid-cols-2">
            <div className="col-span-2 grid gap-6 divide-y-2 divide-gray-100 bg-white px-4 py-6 shadow sm:grid-cols-3 sm:divide-y-0 sm:rounded-lg">
              <div className="col-span-1 text-center">
                <p className="text-lg font-bold">Términos y Condiciones</p>
                <p>Persona Natural</p>
                {documentPnUrl && (
                  <a href={documentPnUrl} target="_blank" rel="noreferrer" className="underline">
                    Descargar
                  </a>
                )}

                <div className="relative mx-4 mt-4 flex max-w-lg flex-col items-center justify-evenly space-y-4 rounded-md border-2 border-dashed border-gray-300 px-2 pt-5 pb-6 sm:flex-row sm:space-y-0">
                  <div className="space-y-1 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="inp_document_file_pn"
                        className="relative mx-auto cursor-pointer rounded-md bg-white font-medium text-primary-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-primary-500 focus-within:ring-offset-2 hover:text-primary-500"
                      >
                        <span>{documentPn ? 'Cambiar archivo' : 'Escoger archivo'}</span>
                        <input
                          id="inp_document_file_pn"
                          name="inp_document_file_pn"
                          type="file"
                          className="sr-only"
                          accept=".pdf"
                          onChange={(event) => checkFile(event, 'documentPn')}
                        />
                      </label>
                    </div>
                    <p className="max-w-[120px] text-xs text-gray-500">PDF hasta 10MB</p>
                  </div>
                  {documentPn && (
                    <p className="max-h-64 overflow-hidden break-all rounded-lg border-2 border-primary-400 p-4 sm:max-w-[50%]">
                      <DocumentTextIcon className="mx-auto mb-2 h-8 w-8 text-gray-400" />
                      {documentPn.name}
                    </p>
                  )}
                </div>

                <SolidButton
                  color={BUTTON_COLORS.BLUE}
                  isLoading={loading}
                  className="mt-6 w-auto px-12"
                  onClick={() => saveFileInfo('tyc_pn')}
                >
                  Guardar cambios
                </SolidButton>
              </div>

              <div className="col-span-1 pt-4 text-center sm:pt-0">
                <p className="text-lg font-bold">Términos y Condiciones</p>
                <p>Persona Jurídica</p>
                {documentPjUrl && (
                  <a href={documentPjUrl} target="_blank" rel="noreferrer" className="underline">
                    Descargar
                  </a>
                )}

                <div className="relative mx-4 mt-4 flex max-w-lg flex-col items-center justify-evenly space-y-4 rounded-md border-2 border-dashed border-gray-300 px-2 pt-5 pb-6 sm:flex-row sm:space-y-0">
                  <div className="space-y-1 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="inp_document_file_pj"
                        className="relative mx-auto cursor-pointer rounded-md bg-white font-medium text-primary-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-primary-500 focus-within:ring-offset-2 hover:text-primary-500"
                      >
                        <span>{documentPj ? 'Cambiar archivo' : 'Escoger archivo'}</span>
                        <input
                          id="inp_document_file_pj"
                          name="inp_document_file_pj"
                          type="file"
                          className="sr-only"
                          accept=".pdf"
                          onChange={(event) => checkFile(event, 'documentPj')}
                        />
                      </label>
                    </div>
                    <p className="max-w-[120px] text-xs text-gray-500">PDF hasta 10MB</p>
                  </div>
                  {documentPj && (
                    <p className="max-h-64 overflow-hidden break-all rounded-lg border-2 border-primary-400 p-4 sm:max-w-[50%]">
                      <DocumentTextIcon className="mx-auto mb-2 h-8 w-8 text-gray-400" />
                      {documentPj.name}
                    </p>
                  )}
                </div>

                <SolidButton
                  color={BUTTON_COLORS.BLUE}
                  isLoading={loading}
                  className="mt-6 w-auto px-12"
                  onClick={() => saveFileInfo('tyc_pj')}
                >
                  Guardar cambios
                </SolidButton>
              </div>

              <div className="col-span-1 pt-4 text-center sm:pt-0">
                <p className="mb-6 text-lg font-bold">Política de Privacidad</p>
                {documentPrivacyUrl && (
                  <a
                    href={documentPrivacyUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="underline"
                  >
                    Descargar
                  </a>
                )}

                <div className="relative mx-4 mt-4 flex max-w-lg flex-col items-center justify-evenly space-y-4 rounded-md border-2 border-dashed border-gray-300 px-2 pt-5 pb-6 sm:flex-row sm:space-y-0">
                  <div className="space-y-1 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="inp_document_file_privacy"
                        className="relative mx-auto cursor-pointer rounded-md bg-white font-medium text-primary-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-primary-500 focus-within:ring-offset-2 hover:text-primary-500"
                      >
                        <span>{documentPrivacy ? 'Cambiar archivo' : 'Escoger archivo'}</span>
                        <input
                          id="inp_document_file_privacy"
                          name="inp_document_file_privacy"
                          type="file"
                          className="sr-only"
                          accept=".pdf"
                          onChange={(event) => checkFile(event, 'documentPrivacy')}
                        />
                      </label>
                    </div>
                    <p className="max-w-[120px] text-xs text-gray-500">PDF hasta 10MB</p>
                  </div>
                  {documentPrivacy && (
                    <p className="max-h-64 overflow-hidden break-all rounded-lg border-2 border-primary-400 p-4 sm:max-w-[50%]">
                      <DocumentTextIcon className="mx-auto mb-2 h-8 w-8 text-gray-400" />
                      {documentPrivacy.name}
                    </p>
                  )}
                </div>

                <SolidButton
                  color={BUTTON_COLORS.BLUE}
                  isLoading={loading}
                  className="mt-6 w-auto px-12"
                  onClick={() => saveFileInfo('pp')}
                >
                  Guardar cambios
                </SolidButton>
              </div>
            </div>
            <div className="col-span-1 mt-8 bg-white shadow sm:mt-0 sm:rounded-lg">
              <div className="bg-primary-600 py-2 px-4 text-center text-lg text-white sm:rounded-t-lg">
                Quienes Somos
              </div>
              <div className="space-y-6 px-4 py-6">
                {buildBaseInput('Contenido', 'textarea', 'about', REQUIRED_RULE, {})}
              </div>
              <div className="flex justify-center bg-gray-50 py-4 px-8 sm:rounded-b-lg">
                <SolidButton
                  color={BUTTON_COLORS.BLUE}
                  isLoading={loading}
                  className="w-auto px-12"
                  onClick={() => saveInfo('about')}
                >
                  Guardar cambios
                </SolidButton>
              </div>
            </div>
            <div className="col-span-1 mt-4 bg-white shadow sm:mt-0 sm:rounded-lg">
              <div className="bg-primary-600 py-2 px-4 text-center text-lg text-white sm:rounded-t-lg">
                Línea 1
              </div>
              <div className="space-y-6 px-4 py-6">
                {buildBaseInput('Contenido', 'textarea', 'fl1', REQUIRED_RULE, {})}
              </div>
              <div className="flex justify-center bg-gray-50 py-4 px-8 sm:rounded-b-lg">
                <SolidButton
                  color={BUTTON_COLORS.BLUE}
                  isLoading={loading}
                  className="w-auto px-12"
                  onClick={() => saveInfo('fl1')}
                >
                  Guardar cambios
                </SolidButton>
              </div>
            </div>
            <div className="col-span-1 mt-4 bg-white shadow sm:mt-0 sm:rounded-lg">
              <div className="bg-primary-600 py-2 px-4 text-center text-lg text-white sm:rounded-t-lg">
                Línea 2
              </div>
              <div className="space-y-6 px-4 py-6">
                {buildBaseInput('Contenido', 'textarea', 'fl2', REQUIRED_RULE, {})}
              </div>
              <div className="flex justify-center bg-gray-50 py-4 px-8 sm:rounded-b-lg">
                <SolidButton
                  color={BUTTON_COLORS.BLUE}
                  isLoading={loading}
                  className="w-auto px-12"
                  onClick={() => saveInfo('fl2')}
                >
                  Guardar cambios
                </SolidButton>
              </div>
            </div>
            <div className="col-span-1 mt-4 bg-white shadow sm:mt-0 sm:rounded-lg">
              <div className="bg-primary-600 py-2 px-4 text-center text-lg text-white sm:rounded-t-lg">
                Banner
              </div>
              <div className="space-y-6 px-4 py-6">
                {buildBaseInput('Contenido', 'textarea', 'banner', REQUIRED_RULE, {})}
              </div>
              <div className="flex justify-center bg-gray-50 py-4 px-8 sm:rounded-b-lg">
                <SolidButton
                  color={BUTTON_COLORS.BLUE}
                  isLoading={loading}
                  className="w-auto px-12"
                  onClick={() => saveInfo('banner')}
                >
                  Guardar cambios
                </SolidButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PrivateRoute>
  );
}
